import { Network } from 'config/types'
import Column from 'layout/Components/Column'
import { RowMiddle } from 'layout/Components/Row'
import { useCallback } from 'react'
import { isMobile } from 'react-device-detect'
import { Trans, useTranslation } from 'react-i18next'
import Image from 'UIKit/Image'
import Text from 'UIKit/Text'
import { filterNetworkSearch } from 'utils/network'
import {
  EmptyCurrencyResponse,
  EmptyMessageEnum,
  PaymentAppSearch,
  PaymentFormControl,
  PaymentWrapOption,
} from 'views/Payment/MobilePayment/components/styles'
import {
  PaymentSelect,
  PaymentSelectProps,
  PaymentSelectTrigger,
  SelectContentInjectedProps,
  SelectScroll,
} from '../PaymentSelect'
import { useFilterOptionsBySearch } from '../PaymentSelect/hooks'

const NetworkDropdownContent = ({
  options,
  onSelect,
}: SelectContentInjectedProps<Network> & {
  value: Network
}) => {
  const {
    onChange,
    options: formatOptions,
    typedValue,
  } = useFilterOptionsBySearch({
    options,
    filterFn: filterNetworkSearch,
  })
  const { t } = useTranslation()

  return (
    <Column maxWidth="352px" gap="10px">
      <PaymentAppSearch autoFocus={!isMobile} placeholder={t('Search Coins')} onValueChanged={onChange} />
      <SelectScroll gap="8px">
        {formatOptions.length > 0
          ? formatOptions.map((network) => (
              <PaymentWrapOption onClick={() => onSelect(network)} key={network.code}>
                <Image
                  src={network.networkInfo.icon}
                  width={28}
                  height={28}
                  style={{
                    minWidth: 28,
                  }}
                />
                <Text bold small color="var(--pm-label-color)">
                  {network.networkInfo.displayName}
                </Text>
              </PaymentWrapOption>
            ))
          : typedValue && <EmptyCurrencyResponse message={EmptyMessageEnum.Network} />}
      </SelectScroll>
    </Column>
  )
}

const NetworkSelectDropdown: React.FC<PaymentSelectProps<Network>> = ({
  options = [],
  onValueChange,
  value,
  disabled,
}) => {
  const { t } = useTranslation()
  const RenderContent = useCallback(
    (props) => {
      return <NetworkDropdownContent value={value} {...props} />
    },
    [value],
  )

  return (
    <PaymentFormControl
      state={{
        errors: [],
        value: '',
        isDirty: false,
      }}
      formatErrorMessage={() => ''}
      label={t('Network')}
      disabled={disabled}
    >
      <PaymentSelect<Network>
        disabled={disabled}
        options={options}
        onSelect={onValueChange}
        DropdownContent={RenderContent}
      >
        <PaymentSelectTrigger>
          {value ? (
            <RowMiddle gap="8px" id="token-select-item">
              <Image
                src={value.networkInfo.icon}
                width={20}
                height={20}
                style={{
                  minWidth: 20,
                }}
              />
              <Text bold small color="var(--pm-color)">
                {value.networkInfo.displayName}
              </Text>
            </RowMiddle>
          ) : (
            <Text small bold color="var(--pm-label-color)">
              <Trans>Select Network</Trans>
            </Text>
          )}
        </PaymentSelectTrigger>
      </PaymentSelect>
    </PaymentFormControl>
  )
}

export default NetworkSelectDropdown
